// Type-only import: importing the FeeSpeed enum as a value would create a
// runtime cycle, since signAccountOp.ts imports this file.
import type { FeeSpeed } from '../../controllers/signAccountOp/signAccountOp'
import { FeePaymentOption } from '../estimate/interfaces'

/**
 * Identifies a fee option across estimations. It is a descriptor rather than a
 * `FeePaymentOption` because the option objects carry per-estimation data
 * (available amount, gas used) that is meaningless once the estimation is gone.
 */
export interface FeeOptionPreference {
  paidBy: string
  tokenAddress: string
  tokenSymbol: string
  isGasTank: boolean
}

/**
 * What the user last picked in the "Pay fee with" section: the fee option and
 * the fee speed. The two are stored separately because the user may set only
 * one of them, and because a stale option must not discard a still-valid speed.
 */
export interface FeeSelectionPreference {
  option?: FeeOptionPreference
  speed?: FeeSpeed
}

/** Keyed by account + chain, since the available fee options differ per both. */
export type FeeSelectionPreferences = { [accountAndChainKey: string]: FeeSelectionPreference }

export const getFeeSelectionPreferenceKey = (
  accountAddr: string,
  chainId: bigint | string
): string => `${accountAddr.toLowerCase()}:${chainId.toString()}`

export const getFeeOptionPreference = (option: FeePaymentOption): FeeOptionPreference => ({
  paidBy: option.paidBy,
  tokenAddress: option.token.address,
  tokenSymbol: option.token.symbol,
  isGasTank: !!option.token.flags.onGasTank
})

export const areFeeOptionPreferencesEqual = (
  a?: FeeOptionPreference,
  b?: FeeOptionPreference
): boolean => {
  if (!a || !b) return false

  return (
    a.paidBy.toLowerCase() === b.paidBy.toLowerCase() &&
    a.tokenAddress.toLowerCase() === b.tokenAddress.toLowerCase() &&
    a.tokenSymbol.toLowerCase() === b.tokenSymbol.toLowerCase() &&
    a.isGasTank === b.isGasTank
  )
}

export const areFeeSelectionPreferencesEqual = (
  a?: FeeSelectionPreference,
  b?: FeeSelectionPreference
): boolean => {
  if (!a || !b) return false

  return a.speed === b.speed && areFeeOptionPreferencesEqual(a.option, b.option)
}

/**
 * Resolves a stored preference back to one of the currently available fee
 * options. Returns undefined when the option no longer exists (token sold,
 * gas tank emptied, EOA payer removed, ...), which is the signal for the
 * caller to fall back to the default selection.
 */
export const findFeeOptionByPreference = (
  options: FeePaymentOption[],
  preference?: FeeOptionPreference
): FeePaymentOption | undefined => {
  if (!preference) return undefined

  return options.find((option) =>
    areFeeOptionPreferencesEqual(getFeeOptionPreference(option), preference)
  )
}
